import { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { safeArray } from '../../../utils/safe';
import type * as yup from 'yup';
import { useSearchParams } from 'react-router-dom';

const COLLECTION_METADATA = {
  orders: {
    label: 'Pedidos (orders)',
    fields: [
      { value: 'total', label: 'Total de la orden' }
    ],
    groupBy: [
      { value: 'status', label: 'Estado del pedido' },
      { value: 'payment_status', label: 'Estado del pago' },
      { value: 'fulfillment_type', label: 'Tipo de entrega' },
      { value: 'destination', label: 'Destino' },
      { value: 'table_id', label: 'Mesa' }
    ],
    defaultField: 'total',
    defaultGroupBy: 'status'
  },
  order_items: {
    label: 'Detalles de pedidos (order_items)',
    fields: [
      { value: 'quantity', label: 'Cantidad vendida' },
      { value: 'line_subtotal', label: 'Subtotal acumulado' },
      { value: 'base_price', label: 'Precio base del producto' }
    ],
    groupBy: [
      { value: 'product_id', label: 'ID de Producto' },
      { value: 'status', label: 'Estado del ítem' }
    ],
    defaultField: 'line_subtotal',
    defaultGroupBy: 'status'
  },
  products: {
    label: 'Productos (products)',
    fields: [
      { value: 'price', label: 'Precio del producto' }
    ],
    groupBy: [
      { value: 'category_id', label: 'Categoría' },
      { value: 'preparation_area', label: 'Área de preparación' },
      { value: 'active', label: 'Estado activo/inactivo' }
    ],
    defaultField: 'price',
    defaultGroupBy: 'category_id'
  },
  purchase_orders: {
    label: 'Gastos/Compras (purchase_orders)',
    fields: [
      { value: 'total', label: 'Total gastado' },
      { value: 'subtotal', label: 'Subtotal gastado' },
      { value: 'tax', label: 'Impuesto de compra' }
    ],
    groupBy: [
      { value: 'expense_category', label: 'Categoría de gasto' },
      { value: 'supplier_id', label: 'Proveedor' }
    ],
    defaultField: 'total',
    defaultGroupBy: 'expense_category'
  }
};

export interface FieldConfig { name: string; label: string; type?: 'text' | 'number' | 'select' | 'date'; options?: string[]; placeholder?: string }
interface Props<T extends FieldValues> { defaultValues: T; schema: yup.ObjectSchema<T>; fields: FieldConfig[]; onSubmit: (values: T) => void }

export function FiltersForm<T extends FieldValues>({ defaultValues, schema, fields, onSubmit }: Props<T>) {
  const [search, setSearch] = useSearchParams();
  const form = useForm<T>({ defaultValues: defaultValues as any, resolver: yupResolver(schema) as any });

  useEffect(() => {
    const values: Record<string, any> = { ...defaultValues };
    search.forEach((value, key) => {
      values[key] = value.includes(',') ? value.split(',') : value;
    });
    form.reset(values as T);
  }, [defaultValues, form, search]);

  const selectedCollection = form.watch('collection' as any) as keyof typeof COLLECTION_METADATA | undefined;

  // Auto-ajustar campos cuando la colección cambia
  useEffect(() => {
    if (selectedCollection && COLLECTION_METADATA[selectedCollection]) {
      const meta = COLLECTION_METADATA[selectedCollection];
      const hasField = fields.some(f => f.name === 'field');
      const hasGroupBy = fields.some(f => f.name === 'groupBy');

      if (hasField) {
        const currentField = form.getValues('field' as any);
        const isValidField = meta.fields.some(f => f.value === currentField);
        if (!isValidField) {
          form.setValue('field' as any, meta.defaultField as any);
        }
      }
      if (hasGroupBy) {
        const currentGroupBy = form.getValues('groupBy' as any);
        const isValidGroupBy = meta.groupBy.some(g => g.value === currentGroupBy);
        if (!isValidGroupBy) {
          form.setValue('groupBy' as any, meta.defaultGroupBy as any);
        }
      }
    }
  }, [selectedCollection, fields, form]);

  const submit = (values: T) => {
    const params = new URLSearchParams();
    Object.entries(values).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '')
        params.set(k, Array.isArray(v) ? v.join(',') : String(v));
    });
    setSearch(params, { replace: true });
    onSubmit(values);
  };

  const renderFieldInput = (field: FieldConfig) => {
    const commonClass = "w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm shadow-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition disabled:opacity-50 disabled:bg-gray-50";

    if (field.name === 'collection') {
      return (
        <select className={commonClass} {...form.register(field.name as any)}>
          <option value="">Seleccione colección...</option>
          {Object.entries(COLLECTION_METADATA).map(([key, meta]) => (
            <option key={key} value={key}>{meta.label}</option>
          ))}
        </select>
      );
    }

    if (field.name === 'field') {
      const allowedFields = selectedCollection ? COLLECTION_METADATA[selectedCollection]?.fields : [];
      return (
        <select className={commonClass} disabled={!selectedCollection} {...form.register(field.name as any)}>
          <option value="">{selectedCollection ? 'Seleccione campo...' : 'Primero seleccione colección'}</option>
          {allowedFields?.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      );
    }

    if (field.name === 'groupBy') {
      const allowedGroups = selectedCollection ? COLLECTION_METADATA[selectedCollection]?.groupBy : [];
      return (
        <select className={commonClass} disabled={!selectedCollection} {...form.register(field.name as any)}>
          <option value="">{selectedCollection ? 'Seleccione agrupación...' : 'Primero seleccione colección'}</option>
          {allowedGroups?.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'select') {
      return (
        <select className={commonClass} {...form.register(field.name as any)}>
          <option value="">Seleccione...</option>
          {safeArray(field.options).map((opt: string) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type ?? 'text'}
        placeholder={field.placeholder}
        className={commonClass}
        {...form.register(field.name as any)}
      />
    );
  };

  return (
    <form onSubmit={form.handleSubmit(submit as any)} className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 items-end bg-gray-50/50 p-5 rounded-2xl border border-gray-200/80">
      {fields.map((field) => (
        <label key={field.name} className="flex flex-col gap-1.5 text-sm">
          <span className="font-semibold text-gray-700">{field.label}</span>
          {renderFieldInput(field)}
          {form.formState.errors[field.name]?.message && (
            <span className="text-xs text-red-600 mt-1">{String(form.formState.errors[field.name]?.message)}</span>
          )}
        </label>
      ))}
      <div className="flex">
        <button
          type="submit"
          className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-medium py-2.5 px-4 shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
        >
          Aplicar filtros
        </button>
      </div>
    </form>
  );
}

